// Custom Glide cells: file name (open affordance), stage pill, tier chip, select (lock), alerts badge.
import {
  GridCellKind,
  type CustomCell,
  type CustomRenderer,
  type ProvideEditorComponent,
  type Rectangle,
} from "@glideapps/glide-data-grid";
import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";

export interface Option {
  value: string | null;
  label: string;
  hint?: string;
  solid?: string;
  on?: string;
}

// ── shared drawing helpers ───────────────────────────────────────────────────

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function ellipsize(ctx: CanvasRenderingContext2D, text: string, max: number): string {
  if (ctx.measureText(text).width <= max) return text;
  let lo = 0;
  let hi = text.length;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (ctx.measureText(text.slice(0, mid) + "…").width <= max) lo = mid;
    else hi = mid - 1;
  }
  return text.slice(0, lo) + "…";
}

export function drawPadlock(ctx: CanvasRenderingContext2D, x: number, y: number, color: string) {
  ctx.save();
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineWidth = 1.3;
  ctx.beginPath();
  ctx.arc(x + 4, y + 4, 2.6, Math.PI, 0);
  ctx.stroke();
  roundRect(ctx, x, y + 4, 8, 6, 1.2);
  ctx.fill();
  ctx.restore();
}

// ── File name cell ───────────────────────────────────────────────────────────

export interface FileCellData {
  kind: "file-cell";
  text: string;
  loanId: string;
  readonly: boolean;
  muted: string;
  hasRed: boolean;
}
export type FileCell = CustomCell<FileCellData>;

export const OPEN_ZONE = 30;

export function makeFileRenderer(onOpen: (loanId: string) => void): CustomRenderer<FileCell> {
  return {
    kind: GridCellKind.Custom,
    isMatch: (c: CustomCell): c is FileCell => (c.data as { kind?: string }).kind === "file-cell",
    needsHover: true,
    needsHoverPosition: true,
    draw: (args, cell) => {
      const { ctx, rect, theme, hoverAmount, hoverX } = args;
      const d = cell.data;
      const pad = theme.cellHorizontalPadding;
      const cy = rect.y + rect.height / 2;
      ctx.font = `500 ${theme.baseFontStyle} ${theme.fontFamily}`;
      const right = OPEN_ZONE + (d.readonly ? 14 : 0) + (d.hasRed ? 10 : 0);
      ctx.fillStyle = d.text ? theme.textDark : theme.textLight;
      ctx.textBaseline = "middle";
      const label = d.text || "Untitled loan";
      ctx.fillText(ellipsize(ctx, label, rect.width - pad - right), rect.x + pad, cy + 0.5);
      let x = rect.x + rect.width - OPEN_ZONE;
      if (d.hasRed) {
        ctx.fillStyle = "#D92D20";
        ctx.beginPath();
        ctx.arc(x - 4, cy, 3.5, 0, Math.PI * 2);
        ctx.fill();
        x -= 10;
      }
      if (d.readonly) drawPadlock(ctx, x - 12, cy - 6, d.muted);
      if (hoverAmount > 0) {
        const bx = rect.x + rect.width - OPEN_ZONE + 3;
        const by = cy - 11;
        const inZone = hoverX !== undefined && hoverX > rect.width - OPEN_ZONE;
        ctx.globalAlpha = hoverAmount;
        ctx.fillStyle = inZone ? theme.accentColor : theme.bgCellMedium;
        roundRect(ctx, bx, by, 22, 22, 5);
        ctx.fill();
        ctx.strokeStyle = inZone ? theme.accentFg : theme.textMedium;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        // ↗ open icon
        ctx.moveTo(bx + 7.5, by + 14.5);
        ctx.lineTo(bx + 14.5, by + 7.5);
        ctx.moveTo(bx + 9, by + 7.5);
        ctx.lineTo(bx + 14.5, by + 7.5);
        ctx.lineTo(bx + 14.5, by + 13);
        ctx.stroke();
        ctx.globalAlpha = 1;
      }
      return true;
    },
    onClick: (args) => {
      if (args.posX > args.bounds.width - OPEN_ZONE) {
        args.preventDefault();
        onOpen(args.cell.data.loanId);
      }
      return undefined;
    },
    provideEditor: () => ({
      editor: TextEditor as unknown as ProvideEditorComponent<FileCell>,
      disablePadding: true,
    }),
    onPaste: (val, data) => ({ ...data, text: val }),
    onDelete: (c) => ({ ...c, data: { ...c.data, text: "" } }),
  };
}

const TextEditor: ProvideEditorComponent<FileCell> = (p) => {
  const [v, setV] = useState(p.initialValue ?? p.value.data.text);
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    const len = el.value.length;
    el.setSelectionRange(len, len);
  }, []);
  const done = (move: readonly [-1 | 0 | 1, -1 | 0 | 1]) =>
    p.onFinishedEditing({ ...p.value, data: { ...p.value.data, text: v } }, move);
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      done([0, 1]);
    } else if (e.key === "Tab") {
      e.preventDefault();
      done([e.shiftKey ? -1 : 1, 0]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      p.onFinishedEditing(undefined, [0, 0]);
    }
  };
  return (
    <input
      ref={ref}
      aria-label="File name"
      className="gdg-input w-full min-w-[220px] rounded-sm border-0 bg-transparent px-2 py-2 text-[13px] text-ink outline-none"
      value={v}
      onChange={(e) => setV(e.target.value)}
      onKeyDown={onKey}
    />
  );
};

// ── Select cells (stage, tier, lock) ─────────────────────────────────────────

export interface SelectCellData {
  kind: "select-cell";
  variant: "stage" | "tier" | "plain";
  value: string | null;
  label: string;
  step?: string;
  solid?: string;
  on?: string;
  border?: string;
  options: Option[];
  dark: boolean;
}
export type SelectCell = CustomCell<SelectCellData>;

const TIER_STYLE: Record<string, { fill?: string; stroke: string; text: string; dash?: number[] }> = {
  PIN: { fill: "#16396B", stroke: "#16396B", text: "#FFFFFF" },
  MONEYBALL: { stroke: "#4470A8", text: "#2D5A91" },
  HIGH: { stroke: "#2D5A91", text: "#2D5A91", dash: [3, 2] },
  TBD: { stroke: "#C4CBD5", text: "#535D6C", dash: [1.5, 2] },
};
const TIER_STYLE_DARK: Record<string, { fill?: string; stroke: string; text: string; dash?: number[] }> = {
  PIN: { fill: "#8FB4E6", stroke: "#8FB4E6", text: "#0A1C38" },
  MONEYBALL: { stroke: "#8FB4E6", text: "#8FB4E6" },
  HIGH: { stroke: "#9FB0C6", text: "#9FB0C6", dash: [3, 2] },
  TBD: { stroke: "#7F90A8", text: "#9FB0C6", dash: [1.5, 2] },
};

export const selectRenderer: CustomRenderer<SelectCell> = {
  kind: GridCellKind.Custom,
  isMatch: (c: CustomCell): c is SelectCell => (c.data as { kind?: string }).kind === "select-cell",
  draw: (args, cell) => {
    const { ctx, rect, theme } = args;
    const d = cell.data;
    const pad = theme.cellHorizontalPadding;
    const cy = rect.y + rect.height / 2;
    ctx.textBaseline = "middle";
    if (!d.value) {
      ctx.fillStyle = theme.textLight;
      ctx.font = theme.baseFontFull;
      ctx.fillText("—", rect.x + pad, cy + 0.5);
      return true;
    }
    const size = theme.baseFontFull.match(/(\d+)px/)?.[1] ?? "13";
    const h = Math.min(22, rect.height - 10);
    if (d.variant === "stage") {
      ctx.font = `600 ${Number(size) - 1}px ${theme.fontFamily}`;
      const step = d.step ?? "";
      ctx.font = `500 ${Number(size) - 2}px ${theme.fontFamily}`;
      const stepW = step ? ctx.measureText(step).width + 5 : 0;
      ctx.font = `600 ${Number(size) - 1}px ${theme.fontFamily}`;
      const maxText = rect.width - pad * 2 - 16 - stepW;
      const text = ellipsize(ctx, d.label, maxText);
      const w = ctx.measureText(text).width + 16 + stepW;
      const x = rect.x + pad - 2;
      roundRect(ctx, x, cy - h / 2, w, h, 6);
      ctx.fillStyle = d.solid ?? theme.bgBubble;
      ctx.fill();
      if (d.border) {
        ctx.strokeStyle = d.border;
        ctx.lineWidth = 1;
        ctx.stroke();
      }
      ctx.fillStyle = d.on ?? theme.textDark;
      ctx.fillText(text, x + 8, cy + 0.5);
      const textW = ctx.measureText(text).width;
      if (step) {
        ctx.globalAlpha = 0.72;
        ctx.font = `500 ${Number(size) - 2}px ${theme.fontFamily}`;
        ctx.fillText(step, x + 8 + textW + 5, cy + 0.5);
        ctx.globalAlpha = 1;
      }
      return true;
    }
    if (d.variant === "tier") {
      const st = (d.dark ? TIER_STYLE_DARK : TIER_STYLE)[d.value] ?? TIER_STYLE.TBD;
      ctx.font = `600 ${Number(size) - 1}px ${theme.fontFamily}`;
      const w = ctx.measureText(d.label).width + 18;
      const x = rect.x + pad - 2;
      roundRect(ctx, x + 0.5, cy - h / 2 + 0.5, w, h - 1, h / 2);
      if (st.fill) {
        ctx.fillStyle = st.fill;
        ctx.fill();
      }
      ctx.setLineDash(st.dash ?? []);
      ctx.strokeStyle = st.stroke;
      ctx.lineWidth = 1.2;
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = st.text;
      ctx.fillText(d.label, x + 9, cy + 0.5);
      return true;
    }
    ctx.font = theme.baseFontFull;
    ctx.fillStyle = theme.textDark;
    ctx.fillText(ellipsize(ctx, d.label, rect.width - pad * 2), rect.x + pad, cy + 0.5);
    return true;
  },
  provideEditor: () => ({
    editor: SelectEditor,
    disablePadding: true,
    deletedValue: (c) => ({ ...c, data: { ...c.data, value: null, label: "" } }),
  }),
  onPaste: (val, data) => {
    const v = val.trim().toLowerCase();
    const opt = data.options.find((o) => o.label.toLowerCase() === v || (o.value ?? "").toLowerCase() === v);
    return opt ? { ...data, value: opt.value, label: opt.label } : undefined;
  },
  onDelete: (c) => ({ ...c, data: { ...c.data, value: null, label: "" } }),
};

const SelectEditor: ProvideEditorComponent<SelectCell> = (p) => {
  const d = p.value.data;
  const [q, setQ] = useState(p.initialValue ?? "");
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return d.options;
    if (/^\d{1,2}$/.test(s) && d.variant === "stage") return d.options.filter((o) => o.hint === `${s}/14`);
    const words = (l: string) =>
      l
        .toLowerCase()
        .replace(/[–—-]/g, " ")
        .split(/\s+/)
        .filter(Boolean);
    return d.options.filter((o) => {
      const l = o.label.toLowerCase();
      const acr = words(o.label)
        .map((w) => (w.length <= 3 ? w : w[0]))
        .join("");
      return l.includes(s) || l.startsWith(s) || acr.startsWith(s.replace(/\s/g, "")) || (o.value ?? "").toLowerCase() === s;
    });
  }, [q, d.options, d.variant]);
  const startIdx = Math.max(0, filtered.findIndex((o) => o.value === d.value));
  const [idx, setIdx] = useState(q ? 0 : startIdx);
  const listRef = useRef<HTMLUListElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => inputRef.current?.focus(), []);
  useEffect(() => {
    const el = listRef.current?.children[idx] as HTMLElement | undefined;
    el?.scrollIntoView({ block: "nearest" });
  }, [idx]);
  const choose = (o: Option | undefined, move: readonly [-1 | 0 | 1, -1 | 0 | 1]) => {
    if (!o) return p.onFinishedEditing(undefined, [0, 0]);
    p.onFinishedEditing({ ...p.value, data: { ...d, value: o.value, label: o.label } }, move);
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setIdx((i) => Math.min(filtered.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setIdx((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      choose(filtered[idx], [0, 1]);
    } else if (e.key === "Tab") {
      e.preventDefault();
      choose(filtered[idx], [e.shiftKey ? -1 : 1, 0]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      p.onFinishedEditing(undefined, [0, 0]);
    }
  };
  return (
    <div className="w-60 rounded-[10px] border border-line bg-raised p-1 text-[13px] text-ink shadow-[var(--shadow-2)]" onKeyDown={onKey}>
      <input
        ref={inputRef}
        role="combobox"
        aria-expanded="true"
        aria-controls="gdg-select-list"
        aria-activedescendant={filtered[idx] ? `gdg-opt-${idx}` : undefined}
        aria-label="Filter options"
        placeholder="Type to filter…"
        className="mb-1 w-full rounded-sm border border-input bg-surface px-2 py-1.5 text-[13px] text-ink outline-none focus:border-focus"
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setIdx(0);
        }}
      />
      <ul id="gdg-select-list" role="listbox" ref={listRef} className="max-h-72 overflow-auto">
        {filtered.map((o, i) => (
          <li
            id={`gdg-opt-${i}`}
            key={o.value ?? "__none"}
            role="option"
            aria-selected={i === idx}
            className={`flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 ${i === idx ? "bg-selected" : ""}`}
            onMouseEnter={() => setIdx(i)}
            onMouseDown={(e) => {
              e.preventDefault();
              choose(o, [0, 0]);
            }}
          >
            {o.solid ? (
              <span className="inline-block h-3 w-3 shrink-0 rounded-sm border border-black/10" style={{ background: o.solid }} />
            ) : null}
            <span className="flex-1 truncate">{o.label}</span>
            {o.value === d.value ? <span aria-hidden>✓</span> : null}
            {o.hint ? <span className="text-[11px] text-ink-3 tnum">{o.hint}</span> : null}
          </li>
        ))}
        {filtered.length === 0 ? <li className="px-2 py-1.5 text-ink-3">No match</li> : null}
      </ul>
    </div>
  );
};

// ── Alerts badge ─────────────────────────────────────────────────────────────

export interface AlertsCellData {
  kind: "alerts-cell";
  red: number;
  amber: number;
  info: number;
  colors: { red: string; amber: string; onRed: string; onAmber: string };
}
export type AlertsCell = CustomCell<AlertsCellData>;

export const alertsRenderer: CustomRenderer<AlertsCell> = {
  kind: GridCellKind.Custom,
  isMatch: (c: CustomCell): c is AlertsCell => (c.data as { kind?: string }).kind === "alerts-cell",
  draw: (args, cell) => {
    const { ctx, rect, theme } = args;
    const d = cell.data;
    const cy = rect.y + rect.height / 2;
    let x = rect.x + theme.cellHorizontalPadding;
    ctx.textBaseline = "middle";
    ctx.textAlign = "center";
    ctx.font = `700 11px ${theme.fontFamily}`;
    const badge = (n: number, fill: string, fg: string, triangle: boolean) => {
      if (!n) return;
      if (triangle) {
        ctx.beginPath();
        ctx.moveTo(x + 9, cy - 9);
        ctx.lineTo(x + 19, cy + 8);
        ctx.lineTo(x - 1, cy + 8);
        ctx.closePath();
      } else {
        ctx.beginPath();
        ctx.arc(x + 9, cy, 9, 0, Math.PI * 2);
      }
      ctx.fillStyle = fill;
      ctx.fill();
      ctx.fillStyle = fg;
      ctx.fillText(String(n), x + 9, cy + (triangle ? 2.5 : 0.5));
      x += 24;
    };
    badge(d.red, d.colors.red, d.colors.onRed, false);
    badge(d.amber, d.colors.amber, d.colors.onAmber, true);
    ctx.textAlign = "left";
    if (!d.red && !d.amber) {
      ctx.fillStyle = theme.textLight;
      ctx.font = theme.baseFontFull;
      ctx.fillText(d.info ? "·" : "", rect.x + theme.cellHorizontalPadding, cy);
    }
    return true;
  },
};

export type { Rectangle };
